<template>
  <Paper
    class="feature-card"
    :class="classes"
    :elevation="1"
    :color="bgColor"
    :style="styles"
    ref="root"
  >
    <div class="feature-card__inner">
      <div class="feature-card__content">
        <FancyIconCircle
          v-if="$slots.icon"
          class="feature-card__icon"
          :color="textColor || 'primary'"
          :size="isLargeScreen ? '2rem' : '1.5rem'"
        >
          <slot name="icon" />
        </FancyIconCircle>
        <VText class="feature-card__title" component="h3" variant="h2">{{
          title
        }}</VText>
        <div class="feature-card__text">
          <slot />
        </div>
        <div v-if="$slots.actions" class="feature-card__actions">
          <slot name="actions" />
        </div>
      </div>
      <div class="feature-card__img" v-if="$slots.img">
        <slot name="img" />
      </div>
    </div>
  </Paper>
</template>

<script>
import { computed, ref, watch } from '@nuxtjs/composition-api';
import {
  Paper,
  VText,
  FancyIconCircle,
  useTheme,
  getThemeColor,
  getTextColor,
  useIsVisible,
} from '@campsite-bio/component-lib';
import { useMediaQuery } from '@vueuse/core';

export default {
  components: {
    Paper,
    VText,
    FancyIconCircle,
  },

  props: {
    title: {
      type: String,
      required: true,
    },

    bgColor: {
      type: String,
      default: 'gray100',
    },

    color: {
      type: String,
      default: null,
    },

    span: {
      type: [Number, String],
      default: null,
    },
  },

  setup(props) {
    const root = ref(null);
    const theme = useTheme();

    const isLargeScreen = useMediaQuery('(min-width: 768px)');
    const isVisible = useIsVisible(root);

    const classes = computed(() => {
      return {
        'feature-card--span-2': +props.span === 2,
      };
    });

    const textColor = computed(() => {
      return props.color ? getTextColor(props.color, theme.value) : null;
    });

    const styles = computed(() => {
      return {
        '--c-bg-color': getThemeColor(props.bgColor, theme.value),
        '--c-text-color': textColor.value,
      };
    });

    watch(isVisible, (val) => {
      if (val) root.value.$el.querySelector('video')?.play().catch(() => {});
    });

    return { classes, styles, textColor, isLargeScreen, root };
  },
};
</script>

<style lang="scss" scoped>
.feature-card {
  color: var(--c-text-color);
  text-align: left;

  &--span-2 {
    @media (min-width: 768px) {
      grid-column: span 2;
    }
  }

  &__inner,
  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__content {
    padding: 2rem;
  }

  &__text {
    flex-grow: 1;
  }

  &__actions {
    margin-top: 1.25rem;
  }
}

.feature-card.feature-card {
  display: flex;
}

.feature-card--span-2 {
  .feature-card__inner {
    display: grid;

    @media (min-width: 768px) {
      grid-template-columns: 0.5fr 1fr;
    }
  }

  .feature-card__img {
    padding: 1rem;
    margin-bottom: -2rem;
    overflow: hidden;
    border-radius: var(--g-border-radius-standard);
    border-radius: Min(
      var(--g-border-radius-standard),
      var(--g-border-radius-min)
    );

    @media (min-width: 768px) {
      align-self: center;
      margin-right: -2rem;
      margin-bottom: unset;
    }

    ::v-deep img,
    ::v-deep video {
      background-color: var(--c-bg-color);
      border-radius: inherit;
      border: 0.225rem solid white;
      margin: 0;
      object-fit: cover;
    }
  }
}

.feature-card__icon.feature-card__icon {
  margin-bottom: 1.5rem;
}

.feature-card__title.feature-card__title {
  margin-bottom: 1rem;
}

.feature-card__title.feature-card__title,
.feature-card__text ::v-deep p {
  color: inherit;
}
</style>
